'use client'

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import HowItWorksHeader from './header'
import HowItWorksPanel from './panel'
import { AUTO_ADVANCE_MS, COPIES, STEP_CONTENT, TRANSITION_MS } from './steps'
import HowItWorksTabs from './tabs'

const HowItWorks = () => {
  const { t } = useTranslation()
  const steps = STEP_CONTENT.map(step => ({
    title: t(step.titleKey),
    description: t(step.descriptionKey),
    image: step.image,
  }))
  const count = steps.length
  const middleStart = count * Math.floor(COPIES / 2)

  const [active, setActive] = useState(middleStart)
  const [offset, setOffset] = useState(0)
  const [animate, setAnimate] = useState(false)
  const [ready, setReady] = useState(false)
  const [paused, setPaused] = useState(false)
  const [interaction, setInteraction] = useState(0)
  const [runId, setRunId] = useState(0)
  const [reduceMotion, setReduceMotion] = useState(false)

  const viewportRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([])
  const activeRef = useRef(active)

  activeRef.current = active
  const stepIndex = ((active % count) + count) % count

  const measure = useCallback((index: number) => {
    const viewport = viewportRef.current
    const button = buttonRefs.current[index]
    if (!viewport || !button) return null
    return viewport.clientWidth / 2 - (button.offsetLeft + button.offsetWidth / 2)
  }, [])

  const applyOffset = useCallback((next: number | null) => {
    if (next == null) return
    setOffset(current => (Math.abs(current - next) < 0.5 ? current : next))
  }, [])

  useLayoutEffect(() => {
    applyOffset(measure(middleStart))
    setReady(true)
  }, [applyOffset, measure, middleStart])

  useEffect(() => {
    if (!ready) return
    applyOffset(measure(active))
  }, [active, applyOffset, measure, ready])

  useEffect(() => {
    const viewport = viewportRef.current
    const track = trackRef.current
    if (!viewport || !track) return

    const recenter = () => applyOffset(measure(activeRef.current))
    const observer = new ResizeObserver(recenter)
    observer.observe(viewport)
    observer.observe(track)
    document.fonts?.ready.then(recenter).catch(() => undefined)

    return () => observer.disconnect()
  }, [applyOffset, measure])

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduceMotion(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    const onVisibility = () => setPaused(document.hidden)
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  useEffect(() => {
    const normalized = middleStart + stepIndex
    if (normalized === active) return
    const delay = animate && !reduceMotion ? TRANSITION_MS + 80 : 0
    const id = window.setTimeout(() => {
      setAnimate(false)
      setActive(normalized)
    }, delay)
    return () => window.clearTimeout(id)
  }, [active, animate, middleStart, reduceMotion, stepIndex])

  useEffect(() => {
    if (paused) return
    setRunId(current => current + 1)
    const id = window.setInterval(() => {
      setAnimate(true)
      setActive(current => current + 1)
    }, AUTO_ADVANCE_MS)
    return () => window.clearInterval(id)
  }, [interaction, paused])

  const goBy = (delta: number) => {
    setAnimate(true)
    setActive(current => {
      const next = current + delta
      const last = count * COPIES - 1
      if (next < 0 || next > last) {
        const currentStep = ((current % count) + count) % count
        return middleStart + currentStep + delta
      }
      return next
    })
    setInteraction(value => value + 1)
  }

  const goTo = (index: number) => {
    setAnimate(true)
    setActive(index)
    setInteraction(value => value + 1)
  }

  return (
    <section className='overflow-x-clip bg-white py-8' aria-labelledby='how-it-works-title' data-track-section='how_it_works'>
      <div className='mx-auto max-w-5xl px-4'>
        <HowItWorksHeader steps={steps} stepIndex={stepIndex} />
        <HowItWorksTabs
          steps={steps}
          active={active}
          count={count}
          middleStart={middleStart}
          offset={offset}
          ready={ready}
          motion={animate && !reduceMotion}
          label={t('Landing.Text.HowItWorksHeading')}
          viewportRef={viewportRef}
          trackRef={trackRef}
          buttonRefs={buttonRefs}
          onSelect={goTo}
          onStep={goBy}
          onPause={setPaused}
        />
        <HowItWorksPanel steps={steps} stepIndex={stepIndex} paused={paused} runId={runId} labelledBy={`how-it-works-tab-${middleStart + stepIndex}`} onNext={() => goBy(1)} />
      </div>
    </section>
  )
}

export default HowItWorks
